import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Switch,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
  Paper,
  alpha,
  Tooltip,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { importSourcesService } from '../services/importSourcesService';
import { ImportSource } from '../types/api';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TestIcon from '@mui/icons-material/BugReport';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { format } from 'date-fns';
import { AddImportSourceDialog } from '../components/ImportSources/AddImportSourceDialog';
import { EditImportSourceDialog } from '../components/ImportSources/EditImportSourceDialog';

export const ImportSourcesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<ImportSource | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResults, setTestResults] = useState<string[] | null>(null);

  const { data: sources, isLoading } = useQuery({
    queryKey: ['import-sources'],
    queryFn: importSourcesService.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => importSourcesService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
      setDeleteConfirmOpen(false);
      setSourceToDelete(null);
    },
  });

  const triggerMutation = useMutation({
    mutationFn: (id: number) => importSourcesService.trigger(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: number) => importSourcesService.test(id),
    onSuccess: (data) => {
      setTestResults(data.sampleIps || []);
      setTestDialogOpen(true);
    },
  });

  const handleEdit = (source: ImportSource) => {
    setSelectedSource(source);
    setEditDialogOpen(true);
  };

  const handleDelete = (source: ImportSource) => {
    setSourceToDelete(source);
    setDeleteConfirmOpen(true);
  };

  const handleTest = (source: ImportSource) => {
    testMutation.mutate(source.id);
  };

  const handleTrigger = (source: ImportSource) => {
    if (window.confirm(`Manually trigger import from ${source.name}?`)) {
      triggerMutation.mutate(source.id);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
              }}
            >
              <CloudDownloadIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}
              >
                Import Sources
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Configure external blacklist sources for automatic import
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            Add Source
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          {isLoading ? (
            <Box p={6} textAlign="center">
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : !sources || sources.length === 0 ? (
            <Box p={6} textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CloudDownloadIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Import Sources
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure external blacklist sources to automatically import blocked IPs
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                }}
              >
                Add Your First Source
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>URL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Interval</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Last Import</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Enabled</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((source) => (
                  <TableRow
                    key={source.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc' },
                      '& td': { borderColor: 'divider' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight={600} color="text.primary">
                        {source.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={source.url}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                          }}
                        >
                          {source.url}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Blacklist"
                        size="small"
                        sx={{
                          fontWeight: 500,
                          background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {source.importIntervalMinutes} min
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {source.lastError ? (
                        <Chip
                          label="Error"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
                            color: 'white',
                          }}
                        />
                      ) : source.lastSuccess ? (
                        <Chip
                          label="Success"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                            color: 'white',
                          }}
                        />
                      ) : (
                        <Chip
                          label="Not Run"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            backgroundColor: '#e2e8f0',
                            color: '#64748b',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {source.lastImported
                          ? format(new Date(source.lastImported), 'MMM dd, HH:mm')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={source.enabled}
                        disabled
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#10b981',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#10b981',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Test Source">
                          <IconButton
                            size="small"
                            onClick={() => handleTest(source)}
                            sx={{
                              color: '#3b82f6',
                              '&:hover': { backgroundColor: alpha('#3b82f6', 0.1) },
                            }}
                          >
                            <TestIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Trigger Import">
                          <IconButton
                            size="small"
                            onClick={() => handleTrigger(source)}
                            disabled={!source.enabled}
                            sx={{
                              color: '#10b981',
                              '&:hover': { backgroundColor: alpha('#10b981', 0.1) },
                              '&.Mui-disabled': { color: '#cbd5e1' },
                            }}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(source)}
                            sx={{
                              color: '#6366f1',
                              '&:hover': { backgroundColor: alpha('#6366f1', 0.1) },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(source)}
                            sx={{
                              color: '#ef4444',
                              '&:hover': { backgroundColor: alpha('#ef4444', 0.1) },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Card>

      <AddImportSourceDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />

      {selectedSource && (
        <EditImportSourceDialog
          open={editDialogOpen}
          source={selectedSource}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedSource(null);
          }}
        />
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Are you sure you want to delete this import source?
          </Alert>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: '#f8fafc',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              fontWeight: 500,
            }}
          >
            {sourceToDelete?.name}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={() => sourceToDelete && deleteMutation.mutate(sourceToDelete.id)}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Test Results</DialogTitle>
        <DialogContent>
          {testResults && testResults.length > 0 ? (
            <>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Successfully fetched {testResults.length} IP addresses
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  borderRadius: 2,
                  borderColor: 'divider',
                }}
              >
                <List dense>
                  {testResults.slice(0, 100).map((ip, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <ListItemText
                        primary={ip}
                        primaryTypographyProps={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                        }}
                      />
                    </ListItem>
                  ))}
                  {testResults.length > 100 && (
                    <ListItem>
                      <ListItemText
                        secondary={`... and ${testResults.length - 100} more`}
                        secondaryTypographyProps={{ fontStyle: 'italic' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              No IP addresses found
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setTestDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
